import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SearchBox from '../components/SearchBox';
import Footer from '../components/Footer';
import { getPublicStats, getStock } from '../services/api';

// "Yeni Eklenen İlanlar" only appears once there are enough listings to look alive.
const MIN_LISTINGS_FOR_HOME = 8;
const COND_LABEL = { original: 'Orijinal', aftermarket: 'Muadil', used: 'Çıkma' };

// Real counts come from /api/parts/stats/; labels/order defined here.
const STAT_CONFIG = [
  { key: 'suppliers', lbl: 'Kayıtlı Tedarikçi' },
  { key: 'requests',  lbl: 'Toplam Talep'      },
  { key: 'cities',    lbl: 'İl Kapsamı'        },
  { key: 'quotes',    lbl: 'Gelen Teklif'      },
];

const WHY_ITEMS = [
  {
    title: 'Paranız Teslimata Kadar Güvende',
    desc:  'Ödemeniz iyzico güvencesinde tutulur; satıcıya ancak parçayı teslim aldığınızı onayladığınızda aktarılır. Sorun olursa iade talebi açarsınız, ekibimiz inceler.',
  },
  {
    title: 'Kontrollü Satıcılar',
    desc:  'Her tedarikçi vergi levhasını yükler ve hesabı ekibimizce onaylanır. Belgesi kontrol edilen satıcılar "Doğrulanmış satıcı" rozetiyle gösterilir.',
  },
  {
    title: 'Hızlı Sonuç',
    desc:  'Talebiniz saniyeler içinde ilgili tedarikçilere iletilir; teklif geldiğinde e-posta ile haberdar edilirsiniz.',
  },
  {
    title: 'En İyi Fiyat',
    desc:  'Birden fazla tedarikçiden teklif alarak karşılaştırın. Rekabetçi fiyatlarla fazla ödemeyin.',
  },
];

const STEPS = [
  { n: '01', title: 'Aracınızı Seçin',        desc: 'Marka, model ve yıl bilgisini girin.' },
  { n: '02', title: 'Talebinizi Gönderin',     desc: 'Parçayı tarif edin, tedarikçilere iletelim.' },
  { n: '03', title: 'Teklifi Kabul Edin',      desc: 'En iyi fiyatı seçin, parçanız yolda.' },
];

const HomePage = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [stats,    setStats]    = useState(null);
  const [latest,   setLatest]   = useState([]);   // newest stock listings

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    getPublicStats().then(r => setStats(r.data)).catch(() => {});
    getStock({ page: 1 })
      .then(r => { if (r.data.count >= MIN_LISTINGS_FOR_HOME) setLatest(r.data.results.slice(0, 8)); })
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={scrolled} />

      {/* ── HERO ── */}
      <section
        className="relative min-h-screen flex flex-col items-center justify-center px-6 text-center"
        style={{
          backgroundImage: `linear-gradient(to bottom,
            rgba(10,15,30,0.82) 0%,
            rgba(10,15,30,0.65) 50%,
            rgba(255,255,255,1) 100%),
            url('https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1800&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center 40%',
        }}
      >
        {/* Headline */}
        <div className="max-w-2xl mx-auto pt-28 pb-8">
          <h1 className="text-white text-5xl font-bold leading-tight mb-4">
            Doğru Parçayı Bulun,<br />
            <span className="text-yellow-400">Zaman Kazanın</span>
          </h1>
          <p className="text-white/70 text-base mb-10 leading-relaxed max-w-lg mx-auto">
            Otomobil, motosiklet, tekne veya iş makineniz için ihtiyacınız olan parçayı tedarikçilere saniyeler içinde iletin.
          </p>

          {/* 3 Steps — inline, compact, horizontal */}
          <div className="flex items-start justify-center gap-0 mb-10 max-w-xl mx-auto">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.n}>
                <div className="flex flex-col items-center text-center flex-1 px-3">
                  <div className="w-9 h-9 rounded-full border-2 border-blue-400 flex items-center justify-center mb-2 bg-blue-700/30 backdrop-blur-sm">
                    <span className="text-blue-300 text-xs font-bold">{s.n}</span>
                  </div>
                  <p className="text-white text-xs font-semibold mb-0.5">{s.title}</p>
                  <p className="text-white/50 text-xs leading-relaxed">{s.desc}</p>
                </div>
                {i < STEPS.length - 1 && (
                  <div className="mt-4 w-8 flex-shrink-0 flex items-start justify-center">
                    <div className="w-full h-px bg-white/20 mt-4" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* CTA buttons */}
          <div className="flex gap-3 justify-center flex-wrap">
            <button
              onClick={() => navigate('/request')}
              className="bg-blue-700 hover:bg-blue-800 text-white font-semibold px-7 py-3 rounded-xl text-sm transition shadow-lg"
            >
              Parça Talebi Oluştur
            </button>
            <button
              onClick={() => navigate('/stock')}
              className="bg-white/10 hover:bg-white/20 text-white border border-white/25 font-semibold px-7 py-3 rounded-xl text-sm transition backdrop-blur-sm"
            >
              Parça Stokuna Bak
            </button>
          </div>
        </div>

        {/* Search box */}
        <div className="w-full max-w-3xl px-4 mt-4">
          <SearchBox onSearch={() => {}} />
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="bg-blue-700 py-10 px-6">
        <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {STAT_CONFIG.map(s => (
            <div key={s.key}>
              <div className="text-white text-3xl font-bold">
                {stats ? Number(stats[s.key]).toLocaleString('tr-TR') : '—'}
              </div>
              <div className="text-blue-200 text-sm mt-1">{s.lbl}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── SAFE PAYMENT (escrow) ── */}
      <section className="bg-green-50 border-b border-green-100 px-6 py-4">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-center sm:text-left">
          <span className="text-sm font-bold text-green-800">🔒 Paranız teslimata kadar güvende</span>
          <span className="text-sm text-green-700">
            Ödeme iyzico güvencesinde tutulur, satıcıya parçayı teslim aldığınızda aktarılır.
          </span>
        </div>
      </section>

      {/* ── NEWEST LISTINGS ── */}
      {latest.length > 0 && (
        <section className="py-14 px-6 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-end justify-between mb-5">
              <h2 className="text-xl font-bold text-gray-900 border-l-4 border-blue-700 pl-3">Yeni Eklenen İlanlar</h2>
              <button onClick={() => navigate('/stock')} className="text-sm font-semibold text-blue-700 hover:underline">Tüm ilanlar →</button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
              {latest.map(s => (
                <button key={s.id} onClick={() => navigate(`/stock?q=${encodeURIComponent(s.oem_no || s.name)}`)}
                  className="text-left bg-white border border-gray-100 rounded-xl overflow-hidden hover:border-blue-300 hover:shadow-md transition">
                  <div className="aspect-[4/3] bg-gray-100">
                    {s.images?.[0]
                      ? <img src={s.images[0]} alt={s.name} loading="lazy" className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center text-4xl text-gray-300">⚙️</div>}
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 mb-1">{s.name}</h3>
                    <p className="text-xs text-gray-500 truncate">
                      {[[s.car_brand, s.car_model].filter(Boolean).join(' '), COND_LABEL[s.condition], s.city].filter(Boolean).join(' • ')}
                    </p>
                    <p className="text-sm font-bold text-gray-900 mt-1.5">₺{(parseFloat(s.price) || 0).toLocaleString('tr-TR')}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── WHY PARCABUL ── */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">

            {/* Left — heading */}
            <div>
              <span className="text-blue-700 text-xs font-semibold uppercase tracking-widest">Neden OtoParçamıBul?</span>
              <h2 className="text-3xl font-bold text-gray-900 mt-3 mb-4 leading-snug">
                Parça Aramak Bu Kadar Kolay Olabilir
              </h2>
              <p className="text-gray-400 text-sm leading-relaxed mb-8">
                Telefonda saatlerce arama yapmak, bayi bayi dolaşmak artık geride kaldı. OtoParçamıBul ile tek formla tedarikçilere ulaşın.
              </p>
              <button
                onClick={() => navigate('/request')}
                className="bg-blue-700 hover:bg-blue-800 text-white font-semibold px-6 py-3 rounded-xl text-sm transition"
              >
                Hemen Başla →
              </button>
            </div>

            {/* Right — 2x2 feature list */}
            <div className="grid grid-cols-1 gap-6">
              {WHY_ITEMS.map((w, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                    <span className="text-blue-700 text-xs font-bold">0{i + 1}</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm mb-1">{w.title}</h4>
                    <p className="text-gray-400 text-sm leading-relaxed">{w.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── SUPPLIER CTA ── */}
      <section id="supplier-cta" className="py-20 px-6 bg-gray-900">
        <div className="max-w-3xl mx-auto text-center">
          <span className="text-blue-400 text-xs font-semibold uppercase tracking-widest">Tedarikçiler İçin</span>
          <h2 className="text-3xl font-bold text-white mt-3 mb-4">Müşterilere Doğrudan Ulaşın</h2>
          <p className="text-gray-400 text-sm mb-8 leading-relaxed max-w-xl mx-auto">
            Parça taleplerini anında görün. Tekliflerinizi gönderin, işinizi büyütün. Kayıt ücretsiz.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <button
              onClick={() => navigate('/register')}
              className="bg-blue-700 hover:bg-blue-800 text-white font-semibold px-8 py-3 rounded-xl text-sm transition"
            >
              Tedarikçi Olarak Kayıt Ol
            </button>
            <button
              onClick={() => navigate('/login')}
              className="border border-gray-600 text-gray-300 hover:border-gray-400 hover:text-white font-semibold px-8 py-3 rounded-xl text-sm transition"
            >
              Giriş Yap
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;